import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3ChildPageState from './useTreatmentPlanV3ChildPageState';

import type { AppDispatch } from '../../../../../../store';
import type { ChildBackgroundType, ChildSchedulePayload } from '../../../../../common';
import type {
  TreatmentPlanV3ChildDependencies,
  UseTreatmentPlanV3ChildResult,
} from './types';

export default function useTreatmentPlanV3Child() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3ChildPageState();

  return useTreatmentPlanV3ChildWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3ChildWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  ...pageState
}: TreatmentPlanV3ChildDependencies): UseTreatmentPlanV3ChildResult {
  const updateBehaviorIssues = useCallback(
    async (value: string) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3ChildInfo({
          treatmentPlanId,
          data: { child_behavior_issues: value },
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3ChildInfo.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const createBackground = useCallback(
    async (backgroundType: ChildBackgroundType, value: string) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.createTreatmentPlanV3ChildBackground({
          treatmentPlanId,
          data: { background_type: backgroundType, background_description: value },
          getAccessTokenSilently,
        }),
      );
      return actions.createTreatmentPlanV3ChildBackground.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const deleteBackground = useCallback(
    async (backgroundId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3ChildBackground({
          treatmentPlanId,
          backgroundId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3ChildBackground.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const createSchedule = useCallback(
    async (data: ChildSchedulePayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.createTreatmentPlanV3ChildSchedule({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.createTreatmentPlanV3ChildSchedule.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const deleteSchedule = useCallback(
    async (scheduleId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3ChildSchedule({
          treatmentPlanId,
          scheduleId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3ChildSchedule.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    ...pageState,
    updateBehaviorIssues,
    createBackground,
    deleteBackground,
    createSchedule,
    deleteSchedule,
  };
}
